import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Loader, Typography } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../utils/localization';
import { TAB_STATUS_DONE, TAB_STATUS_TODO } from '../constant';

import Step1 from './Step1';
import Step2 from './Step2';

import './ReviewAndSubmitBody.css';

function ReviewAndSubmitBody({
  firstStep,
  isModalOpen,
  isAlertActive,
  setIsAlertActive,
  setDataForSubmitAction,
  retriggerApisForError,
  closeReviewAndSubmitModal,
}) {
  const {
    reviewAndSubmit,
    tabs: { tabData },
  } = useSelector((state) => state?.certifications?.details);

  const l = useInvertedLocalize();

  const todoTabCount = tabData?.find(
    (tab) => tab.key === TAB_STATUS_TODO
  )?.count;
  const doneTabCount = tabData?.find(
    (tab) => tab.key === TAB_STATUS_DONE
  )?.count;
  const [config, setConfig] = useState({});
  const hasZeroCountForItems = todoTabCount === 0;

  useEffect(() => {
    if (reviewAndSubmit) {
      setConfig({
        primaryCertifier: reviewAndSubmit?.primaryCertifier,
        isComplete: hasZeroCountForItems,
        withESignature: reviewAndSubmit?.withESignature,
        isCommentEnabled: reviewAndSubmit?.isCommentEnabled,
        enableloginSignature: reviewAndSubmit?.enableloginSignature,
        todoTabCount,
        doneTabCount,
        step1Data: reviewAndSubmit?.step1Data,
        step2Data: reviewAndSubmit?.step2Data,
        userAttribute: reviewAndSubmit?.userAttribute,
        noOfTries: reviewAndSubmit?.noOfTries,
      });
    }
  }, [todoTabCount, reviewAndSubmit]);

  const step1WrapperRef = useRef(null);
  const step2WrapperRef = useRef(null);

  useEffect(() => {
    if (step1WrapperRef.current) step1WrapperRef.current.inert = !firstStep;
    if (step2WrapperRef.current) step2WrapperRef.current.inert = firstStep;
  }, [firstStep]);

  const reviewAndSubmitModalBody = classnames('ReviewAndSubmitModal-body');

  const renderFailedApiUi = () => (
    <div className='ReviewAndSubmitModal-bodyError'>
      <Typography kind='h2'>{l("Couldn't Load")}</Typography>
      <Typography kind='body2'>
        {l(
          "The Summary didn't load properly due to a network issue. Please refresh or try again later."
        )}
      </Typography>
      <div className='ReviewAndSubmitModal-bodyErrorButtons'>
        <Button
          type='button'
          kind='filled'
          onClick={retriggerApisForError}
          size='medium'>
          {l('Refresh')}
        </Button>
        <Button
          type='button'
          kind='ghost'
          size='medium'
          onClick={closeReviewAndSubmitModal}>
          {l('Close')}
        </Button>
      </div>
    </div>
  );

  const renderLoaderAndSteps = () => {
    if (reviewAndSubmit?.isLoadingReviewAndSubmit) {
      return (
        <>
          <div className='ReviewAndSubmitModal-bodyLoader--loading'>
            {reviewAndSubmit?.reviewAndSubmitError ? (
              renderFailedApiUi()
            ) : (
              <Loader kind='dots' format='inline' color='info' size='medium' />
            )}
          </div>
          <div
            ref={step1WrapperRef}
            className='ReviewAndSubmitModal-bodyStep1--loading'>
            <Step1
              isModalOpen={isModalOpen}
              firstStep={firstStep}
              configData={config}
            />
          </div>
          <div
            ref={step2WrapperRef}
            className='ReviewAndSubmitModal-bodyStep2--loading'>
            <Step2
              isModalOpen={isModalOpen}
              firstStep={firstStep}
              configData={config}
              isAlertActive={isAlertActive}
              setIsAlertActive={setIsAlertActive}
              setDataForSubmitAction={setDataForSubmitAction}
            />
          </div>
        </>
      );
    }

    if (firstStep) {
      return (
        <>
          <div className='ReviewAndSubmitModal-bodyLoader--finished'>
            {reviewAndSubmit?.reviewAndSubmitError ? (
              renderFailedApiUi()
            ) : (
              <Loader kind='dots' format='inline' color='info' size='medium' />
            )}
          </div>
          <div
            ref={step1WrapperRef}
            className='ReviewAndSubmitModal-bodyStep1--visible'>
            <Step1
              isModalOpen={isModalOpen}
              firstStep={firstStep}
              configData={config}
            />
          </div>
          <div
            ref={step2WrapperRef}
            className='ReviewAndSubmitModal-bodyStep2--hidden'>
            <Step2
              isModalOpen={isModalOpen}
              firstStep={firstStep}
              configData={config}
              isAlertActive={isAlertActive}
              setIsAlertActive={setIsAlertActive}
              setDataForSubmitAction={setDataForSubmitAction}
            />
          </div>
        </>
      );
    }

    return (
      <>
        <div className='ReviewAndSubmitModal-bodyLoader--hidden'>
          {reviewAndSubmit?.reviewAndSubmitError ? (
            renderFailedApiUi()
          ) : (
            <Loader kind='dots' format='inline' color='info' size='medium' />
          )}
        </div>
        <div
          ref={step1WrapperRef}
          className='ReviewAndSubmitModal-bodyStep1--hidden'>
          <Step1
            isModalOpen={isModalOpen}
            firstStep={firstStep}
            configData={config}
          />
        </div>
        <div
          ref={step2WrapperRef}
          className='ReviewAndSubmitModal-bodyStep2--visible'>
          <Step2
            isModalOpen={isModalOpen}
            firstStep={firstStep}
            configData={config}
            isAlertActive={isAlertActive}
            setIsAlertActive={setIsAlertActive}
            setDataForSubmitAction={setDataForSubmitAction}
          />
        </div>
      </>
    );
  };

  return (
    <section className={reviewAndSubmitModalBody}>
      {renderLoaderAndSteps()}
    </section>
  );
}

ReviewAndSubmitBody.propTypes = {
  firstStep: PropTypes.bool.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  isAlertActive: PropTypes.bool.isRequired,
  setIsAlertActive: PropTypes.func,
  setDataForSubmitAction: PropTypes.func,
  retriggerApisForError: PropTypes.func.isRequired,
  closeReviewAndSubmitModal: PropTypes.func.isRequired,
};

ReviewAndSubmitBody.defaultProps = {
  setIsAlertActive: () => {},
  setDataForSubmitAction: () => {},
};

export default ReviewAndSubmitBody;
